import { Database, GitCompare, RefreshCw } from "lucide-react";
import React, { useState } from "react";

import type * as api from "@/api/supply";
import { useAuth } from "@/auth/useAuth";
import { Button } from "@/components/ui/button";
import { extractErrorMessage } from "@/lib/api-errors";
import { cn } from "@/lib/utils";
import {
  fmtDate,
  fmtDateTime,
  WB_SOURCE_LABEL as SOURCE_LABEL,
  wbSourceLabel,
} from "./format";
import { useSetWbSource, useWbSource } from "./useWbSource";
import { WbSourceCompare } from "./WbSourceCompare";

/** Регионы, где у остатка витрины есть слабая или неопределённая оценка склада, — «Москва: слабая
 *  12 шт. (3%), не определён 4 шт. (1%)». Доля — от остатка FBO региона. */
function stockQualityLines(q: api.WbStockQuality[]): string[] {
  const pct = (v: number, of: number): string =>
    of > 0 ? ` (${Math.round((100 * v) / of)}%)` : "";
  const pcs = (v: number): string => `${Math.round(v)} шт.`;
  return q
    .filter((r) => r.weak > 0 || r.unresolved > 0)
    .map((r) => {
      const parts: string[] = [];
      if (r.weak > 0) parts.push(`слабая ${pcs(r.weak)}${pct(r.weak, r.fbo_stock)}`);
      if (r.unresolved > 0)
        parts.push(
          `не определён ${pcs(r.unresolved)}${pct(r.unresolved, r.fbo_stock)}`,
        );
      return `${r.region}: ${parts.join(", ")}`;
    });
}

const switchQuestion = (to: api.WbSource): string =>
  `Сменить источник WB на «${SOURCE_LABEL[to]}»?\n\n` +
  "Настройка общая — поменяются цифры у всех: заказы FBO и остаток FBO WB по регионам " +
  "в «Распределении», в движке распределения и в «Производстве». " +
  (to === "mart"
    ? "Если витрина сейчас не даёт отчёт, переключение не произойдёт и будет показана причина."
    : "«В пути», короба и формула «Допоставить» не меняются.");

/** Плашка источника WB «Распределения»: откуда заказы/остаток WB и на какую дату, ошибка отчёта WB
 *  (503 витрины — текстом, а не пустыми строками), переключатель (только админ) и сравнение
 *  старого и нового источника (docs/todo/2026-10-19-distribution-wb-mart.md). */
export function WbSourceBar({
  report,
  error,
  loading,
  fetching,
  params,
}: {
  report: api.WbAnalyticsReport | undefined;
  error: unknown;
  loading: boolean;
  /** идёт (пере)запрос отчёта WB — в т.ч. после смены источника */
  fetching: boolean;
  params: api.CalcParams;
}): React.ReactElement {
  const { user } = useAuth();
  const setting = useWbSource();
  const setMut = useSetWbSource();
  const [compareOpen, setCompareOpen] = useState(false);

  // источник отчёта — из самого отчёта (по нему посчитаны цифры); пока отчёта нет — из настройки
  const shown: api.WbSource | undefined =
    report?.wb_source ?? setting.data?.wb_source;
  const target: api.WbSource | undefined = setting.data
    ? setting.data.wb_source === "mart"
      ? "legacy"
      : "mart"
    : undefined;
  // отчёт посчитан не по тому источнику, что в настройке: только что переключили (идёт пересчёт)
  // или переключил кто-то другой, а отчёт ещё прежний
  const staleSource =
    report?.wb_source != null &&
    setting.data != null &&
    report.wb_source !== setting.data.wb_source;
  const quality =
    report?.wb_source === "mart"
      ? stockQualityLines(report.wb_stock_quality ?? [])
      : [];

  return (
    <div
      data-testid="wb-source-bar"
      className={cn(
        "space-y-1 rounded-md border px-3 py-2 text-sm",
        error
          ? "border-red-300 bg-red-50 dark:border-red-900 dark:bg-red-950/40"
          : "bg-card",
      )}
    >
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
        <Database className="size-4 text-muted-foreground" />
        <span>
          <span className="text-muted-foreground">Источник WB: </span>
          <b data-testid="wb-source-label">
            {shown ? SOURCE_LABEL[shown] : "—"}
          </b>
        </span>
        {report && !report.wb_source && (
          <span className="text-destructive">
            в ответе отчёта WB нет источника данных
          </span>
        )}
        {report && (
          <span className="text-xs text-muted-foreground">
            остаток на{" "}
            <b className="text-foreground">{fmtDate(report.as_of)}</b>
            {" · "}заказы {fmtDate(report.orders_from)} —{" "}
            <b className="text-foreground">{fmtDate(report.orders_to)}</b>
            {report.wb_source === "mart" && (
              <>
                {" · "}сборка витрины{" "}
                <b className="text-foreground">
                  {fmtDateTime(report.mart_built_at)}
                </b>
              </>
            )}
          </span>
        )}
        {report?.wb_source === "mart" && report.mart_stale && (
          <span
            data-testid="wb-mart-stale"
            className="rounded bg-amber-100 px-1.5 py-0.5 text-xs font-medium text-amber-800 dark:bg-amber-950 dark:text-amber-300"
            title="Сегодняшняя сборка dwh не прошла — отчёт посчитан по вчерашней (проверьте DAG dwh_build)"
          >
            данные за вчера (сборка {fmtDateTime(report.mart_built_at)})
          </span>
        )}
        {staleSource && setting.data && (
          <span
            data-testid="wb-source-stale"
            className="flex items-center gap-1 text-xs font-medium text-amber-700 dark:text-amber-400"
          >
            {fetching ? (
              <>
                <RefreshCw className="size-3 animate-spin" /> отчёт по прежнему
                источнику, пересчитываю…
              </>
            ) : (
              <>
                отчёт посчитан по «{wbSourceLabel(report?.wb_source)}», в
                настройке — «{SOURCE_LABEL[setting.data.wb_source]}»: обновите
                данные
              </>
            )}
          </span>
        )}
        {loading && !report && (
          <span className="flex items-center gap-1 text-xs text-muted-foreground">
            <RefreshCw className="size-3 animate-spin" /> считаю спрос WB…
          </span>
        )}
        {setting.data?.updated_by && (
          <span
            className="text-xs text-muted-foreground/70"
            title={fmtDateTime(setting.data.updated_at)}
          >
            переключил {setting.data.updated_by}
          </span>
        )}
        <div className="ml-auto flex flex-wrap items-center gap-2">
          {user?.is_admin && target && (
            <Button
              size="sm"
              variant="outline"
              disabled={setMut.isPending || fetching}
              onClick={() => {
                if (window.confirm(switchQuestion(target)))
                  setMut.mutate(target);
              }}
              title={
                fetching
                  ? "Идёт пересчёт отчёта WB — переключение после него"
                  : "Только админ: общая настройка для «Распределения», движка и «Производства»"
              }
            >
              {(setMut.isPending || fetching) && (
                <RefreshCw className="size-3.5 animate-spin" />
              )}
              {target === "mart"
                ? "Переключить на витрину dwh"
                : "Вернуть старый источник"}
            </Button>
          )}
          <Button
            size="sm"
            variant="outline"
            onClick={() => setCompareOpen(true)}
            title="Оба источника с вашими отсечками: по регионам и топ расхождений товар × регион"
          >
            <GitCompare className="size-3.5" /> Сравнить источники WB
          </Button>
        </div>
      </div>
      {quality.length > 0 && (
        <p
          data-testid="wb-stock-quality"
          className="text-xs text-muted-foreground"
          title="Часть остатка FBO витрины, где склад оценён слабо или не определён: регион такого остатка может быть неточным"
        >
          <span className="text-amber-700 dark:text-amber-400">
            Остаток FBO с неточным складом:
          </span>{" "}
          {quality.join(" · ")}
        </p>
      )}
      {error ? (
        <p className="text-destructive">
          Отчёт WB не получен: {extractErrorMessage(error, "ошибка загрузки")}
        </p>
      ) : null}
      {setting.error ? (
        <p className="text-destructive">
          Настройка источника WB не загрузилась:{" "}
          {extractErrorMessage(setting.error, "ошибка загрузки")}
        </p>
      ) : null}
      {setMut.error ? (
        <p className="text-destructive">
          Источник WB не переключён:{" "}
          {extractErrorMessage(setMut.error, "ошибка запроса")}
        </p>
      ) : null}
      {compareOpen && (
        <WbSourceCompare
          params={params}
          onClose={() => setCompareOpen(false)}
        />
      )}
    </div>
  );
}
